<template>
  <form class="p-card auth-card" @submit.prevent="submitHandler">
    <div class="form-header mt-3 ml-4 mr-4">
      <div class="logo-container">
        <img alt="logo" class="logo" src="assets/layout/images/logo_kron_tm128.png" width="48" />
        <div class="logo-name">Смена пароля</div>
        <Button icon="pi pi-arrow-left" :disabled="loading" @click="router.go(-1)" data-testid="change_password_back" />
      </div>
      <div class="server-name" data-testid="server_name">{{ store.getters.info.login }}</div>
    </div>
    <ProgressBar v-if="loading" mode="indeterminate" style="height: 0.5em" />

    <div class="flex flex-column gap-3 px-4 mt-4">
      <div class="flex flex-column">
        <label for="password" class="sr-only">Старый пароль</label>
        <InputText id="password" type="password" v-model="form.password"
          :class="{ 'p-invalid': !form.password.length, 'mt-1': true }" />
      </div>
      <div class="flex flex-column">
        <label for="passwordNew1" class="sr-only">Новый пароль</label>
        <InputText id="passwordNew1" type="password" class="mt-1" v-model="form.passwordNew1" />
      </div>
      <div class="flex flex-column">
        <label for="passwordNew2" class="sr-only">Подтвердите новый пароль</label>
        <Password id="passwordNew2" v-model="form.passwordNew2" class="mt-1" />
        <div class="mt-1">
          <Message :severity="isValidatePasswordEquality ? 'success' : 'error'" :closable="false">
            Пароли должны совпадать
          </Message>
          <template v-if="checkPasswordRules?.length">
            <Message v-for="rule in checkPasswordRules" :key="rule.text" :closable="false" :severity="rule.severity">
              {{ rule.text }}
            </Message>
          </template>
        </div>
      </div>
    </div>
    <div class="flex justify-content-center mt-2 mb-4 text-center">
      <Button class="submit-button" :disabled="loading ||
        !passwordRules ||
        !isValidatePasswordEquality ||
        invalidPasswordRules ||
        !form.passwordNew2.length ||
        !form.password.length
        " type="submit" label="Сменить пароль" icon="pi pi-unlock" />
    </div>
  </form>
</template>

<script setup>
import { reactive, ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';

const store = useStore();
const router = useRouter();
const toast = useToast();

const form = reactive({
  password: '',
  passwordNew1: '',
  passwordNew2: '',
});
const loading = ref(false);
const passwordRules = ref(null);

const isValidatePasswordEquality = computed(
  () => form.passwordNew1 === form.passwordNew2
);

const checkPasswordRules = computed(() => {
  if (!passwordRules.value) return [];
  const result = [];
  for (const rule of passwordRules.value) {
    if (rule.regexRule) {
      const regex = new RegExp(rule.value, 'u');
      const isValid = regex.test(form.passwordNew2) && form.passwordNew2.length;
      result.push({
        text: rule.errorText,
        severity: isValid ? 'success' : 'error',
      });
    }
  }
  return result;
});

const invalidPasswordRules = computed(() => {
  if (!checkPasswordRules.value.length) return false;
  return checkPasswordRules.value.some(rule => rule.severity === 'error');
});


const loadRules = () => {
  const service = store.getters.nameToService('BaseClassicService');
  service
    .customGet('api/password/rules/', {})
    .then(response => (passwordRules.value = response))
    .catch(error => {
      store.commit('SET_ERROR', {
        er: error,
        source: ['ChangePassword', 'loadRules'],
      });
    });
};

const submitHandler = () => {
  loading.value = true;
  const payload = {
    oldPassword: form.password,
    newPassword: form.passwordNew1,
    confirmPassword: form.passwordNew2,
  };

  const service = store.getters.nameToService('BaseClassicService');
  service
    .customPost('api/password/change/', payload)
    .then(() => {
      store.dispatch('setPasswordIsOld', false);
      toast.add({
        severity: 'success',
        summary: 'Пароль изменён',
        detail: 'Смена пароля прошла успешно. Выполняется выход из системы.',
        life: 3000,
      });
      router.push('/login');
    })
    .catch(error => {
      store.commit('SET_ERROR', {
        er: error,
        source: ['ChangePassword', 'submitHandler'],
      });
    })
    .finally(() => loading.value = false);
};

onMounted(() => {
  const info = store.getters.info;
  if (!info.login || !info.userId) {
    router.push({ name: 'login' });
    return;
  }
  if (info.passwordIsOld === true || router.currentRoute.value.query.type === 'system') {
    toast.add({
      severity: 'warn',
      summary: 'Внимание',
      detail: 'Срок действия вашего пароля истекает. Рекомендуем сменить пароль для безопасности.',
      life: 3000,
    });
  }
  loadRules();
});
</script>

<style lang="scss" scoped>
:deep(.p-password) {
  display: block;
  width: 100%;
}

:deep(.p-password input) {
  width: 100%;
}

.submit-button {
  width: 88%;
  height: 2.5rem;
  font-weight: bold;
}

.server-name {
  font-size: 2rem;
  margin: 0.5rem 0;
  color: var(--text-color);
  font-weight: bold;
}

.form-header {
  text-align: center;
  margin-bottom: 0.7rem;
}

.auth-card {
  min-width: 300px;
  border-radius: 10px;
}

.logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.logo-name {
  font-size: 1.9rem;
  margin: 0;
  color: var(--text-color);
  font-weight: bold;
}

::v-deep(.p-message) {
  margin: 1px;
}

::v-deep(.p-message-wrapper) {
  padding: 0.4rem 1rem;
}
</style>